import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  FaStar,
  FaSmile,
  FaFrown,
  FaMeh,
  FaPaperPlane,
  FaCommentDots,
  FaBug,
  FaLightbulb,
  FaExclamationCircle,
} from "react-icons/fa";

const Feedback = () => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedbackType, setFeedbackType] = useState("general");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    experience: "good",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error("Please add a star rating");
      return;
    }
    toast.success("Thanks — your feedback was received!");
    setRating(0);
    setHover(0);
    setFeedbackType("general");
    setFormData({
      name: "",
      email: "",
      message: "",
      experience: "good",
    });
  };

  const feedbackTypes = [
    { id: "general", label: "General", icon: FaCommentDots },
    { id: "bug", label: "Bug report", icon: FaBug },
    { id: "suggestion", label: "Suggestion", icon: FaLightbulb },
    { id: "complaint", label: "Complaint", icon: FaExclamationCircle },
  ];

  const experienceOptions = [
    {
      value: "excellent",
      label: "Excellent",
      icon: <FaSmile className="text-emerald-500" />,
    },
    {
      value: "good",
      label: "Good",
      icon: <FaSmile className="text-navy-700" />,
    },
    {
      value: "average",
      label: "Average",
      icon: <FaMeh className="text-amber-500" />,
    },
    {
      value: "poor",
      label: "Poor",
      icon: <FaFrown className="text-red-500" />,
    },
  ];

  return (
    <div className="min-h-screen pb-16">
      <section className="relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 px-4 py-14 text-white">
        <div className="pointer-events-none absolute -right-10 top-0 h-56 w-56 rounded-full bg-amber-400/20 blur-3xl" />
        <div className="relative mx-auto max-w-3xl text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-amber-400">
            We listen
          </p>
          <h1 className="font-display text-4xl font-bold md:text-5xl">
            Share your feedback
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-white/75">
            Tell us what works, what breaks, and what would make FixMyRoad more
            useful for your street.
          </p>
        </div>
      </section>

      <div className="relative z-10 mx-auto max-w-5xl px-4 pb-4 pt-6 sm:pt-8">
        <div className="overflow-hidden rounded-3xl border border-navy-900/10 bg-white shadow-[0_24px_60px_rgba(11,31,68,0.12)]">
          <div className="grid md:grid-cols-[0.9fr_1.2fr]">
            <aside className="bg-gradient-to-b from-navy-900 to-navy-800 p-8 text-white md:p-10">
              <h2 className="font-display text-2xl font-semibold">
                Why it matters
              </h2>
              <ul className="mt-6 space-y-4 text-white/80">
                <li className="rounded-xl bg-white/5 p-4">
                  Improves report flow and map clarity
                </li>
                <li className="rounded-xl bg-white/5 p-4">
                  Shapes features the community actually needs
                </li>
                <li className="rounded-xl bg-white/5 p-4">
                  Helps keep road safety tools trustworthy
                </li>
              </ul>
              <div className="mt-8 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm">
                <p className="font-semibold text-amber-300">Need a faster fix?</p>
                <p className="mt-1 text-white/75">
                  For potholes, use the{" "}
                  <Link to="/report" className="text-amber-300 underline">
                    Report
                  </Link>{" "}
                  page instead of this form.
                </p>
              </div>
            </aside>

            <form onSubmit={handleSubmit} className="space-y-6 p-8 md:p-10">
              <div>
                <label className="mb-3 block text-sm font-medium text-navy-900">
                  Rate your experience
                </label>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setRating(value)}
                      onMouseEnter={() => setHover(value)}
                      onMouseLeave={() => setHover(0)}
                      className="rounded-lg p-1 transition hover:scale-110"
                      aria-label={`${value} stars`}
                    >
                      <FaStar
                        className="text-3xl"
                        color={
                          value <= (hover || rating) ? "#f59e0b" : "#d9e2ef"
                        }
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-navy-900">
                  Feedback type
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {feedbackTypes.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setFeedbackType(id)}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                        feedbackType === id
                          ? "border-amber-500 bg-amber-50 text-navy-900"
                          : "border-navy-900/10 hover:border-navy-700/30"
                      }`}
                    >
                      <Icon
                        className={
                          feedbackType === id ? "text-amber-600" : "text-navy-700"
                        }
                      />
                      <span className="text-sm font-medium">{label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-medium text-navy-900">
                  Overall feel
                </label>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {experienceOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex cursor-pointer flex-col items-center rounded-xl border px-3 py-3 transition ${
                        formData.experience === option.value
                          ? "border-amber-500 bg-amber-50"
                          : "border-navy-900/10 hover:border-navy-700/30"
                      }`}
                    >
                      <input
                        type="radio"
                        name="experience"
                        value={option.value}
                        checked={formData.experience === option.value}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      {option.icon}
                      <span className="mt-1 text-xs font-medium text-navy-800">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="name"
                    className="mb-1.5 block text-sm font-medium text-navy-900"
                  >
                    Name (optional)
                  </label>
                  <input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-1.5 block text-sm font-medium text-navy-900"
                  >
                    Email (optional)
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15"
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="mb-1.5 block text-sm font-medium text-navy-900"
                >
                  Your feedback
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  className="w-full rounded-xl border border-navy-900/10 bg-surface px-4 py-3 outline-none focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15"
                  placeholder="What should we improve?"
                />
              </div>

              <button
                type="submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 py-3 font-semibold text-navy-950 transition hover:bg-amber-400"
              >
                <FaPaperPlane />
                Submit feedback
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
